import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import { waLink } from "@/lib/api";
import { Logo } from "@/components/Logo";
import UserMenu from "@/components/UserMenu";
import LanguageToggle from "@/components/LanguageToggle";
import PromoBar from "@/components/PromoBar";

const links = [
  { label: "Puri Tours", href: "#puri-tours" },
  { label: "All Odisha", href: "#odisha-tours" },
  { label: "Places", href: "#places" },
  { label: "Plans", href: "#itineraries" },
  { label: "Rentals", href: "#rentals" },
  { label: "Build Package", href: "#package" },
  { label: "Hotels", href: "#hotels" },
  { label: "Reviews", href: "#reviews" },
];

export const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <>
    <PromoBar />
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-0 z-50 backdrop-blur-xl bg-sand/70 border-b border-ink/10"
      style={{ top: "var(--promo-h, 0px)" }}
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <button
          data-testid="nav-brand"
          onClick={() => window.__lenis?.scrollTo(0)}
          className="flex items-center gap-2 group"
        >
          <Logo size={38} className="group-hover:scale-105 transition-transform duration-300" testId="nav-brand-logo" />
          <span className="font-serif text-xl font-bold tracking-tight text-ink notranslate">
            Random <span className="text-terra italic">Rider</span>
          </span>
        </button>

        <nav className="hidden xl:flex items-center gap-4 2xl:gap-6 whitespace-nowrap">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => scrollTo(l.href)}
              className="text-sm font-medium text-ink/70 hover:text-terra transition-colors duration-200"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <LanguageToggle />
          <UserMenu />
          <Link
            to="/admin/login"
            data-testid="nav-admin-link"
            className="text-xs font-mono uppercase tracking-widest text-ink/50 hover:text-ink transition-colors"
          >
            Admin
          </Link>
          <a
            href={waLink("Hi Random Rider! I want to plan a trip in Puri / Odisha.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-book-whatsapp-button"
            className="flex items-center gap-2 bg-[#25D366] text-white text-sm font-semibold px-4 py-2.5 rounded-full whitespace-nowrap hover:brightness-110 hover:scale-[1.03] transition-all duration-200"
          >
            <MessageCircle size={16} /> Book on WhatsApp
          </a>
        </div>

        <button
          data-testid="nav-mobile-menu-button"
          className="xl:hidden text-ink"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="xl:hidden bg-sand border-t border-ink/10 px-6 py-4 flex flex-col gap-4"
          data-testid="nav-mobile-menu"
        >
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => { scrollTo(l.href); setOpen(false); }}
              className="text-left text-base font-medium text-ink"
            >
              {l.label}
            </button>
          ))}
          <LanguageToggle />
          <UserMenu mobile />
          <a
            href={waLink("Hi Random Rider! I want to plan a trip in Puri / Odisha.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-mobile-whatsapp-button"
            className="flex items-center justify-center gap-2 bg-[#25D366] text-white font-semibold px-4 py-3 rounded-full"
          >
            <MessageCircle size={16} /> Book on WhatsApp
          </a>
          <Link to="/admin/login" data-testid="nav-mobile-admin-link" className="text-xs font-mono uppercase tracking-widest text-ink/50">
            Admin Portal
          </Link>
        </motion.div>
      )}
    </motion.header>
    </>
  );
}
